const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('qnc_token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

export const authAPI = {
  // Login
  login: async (email, password) => {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error } };
    }

    return response.json();
  },

  // Get current user info
  getCurrentUser: async () => {
    const response = await fetch(`${API_URL}/api/auth/me`, {
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to get user info');
    }

    return response.json();
  },

  // Request password reset
  requestPasswordReset: async (email) => {
    const response = await fetch(`${API_URL}/api/auth/password-reset-request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });

    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error } };
    }

    return response.json();
  },

  // Confirm password reset
  confirmPasswordReset: async (token, newPassword) => {
    const response = await fetch(`${API_URL}/api/auth/password-reset-confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, new_password: newPassword })
    });

    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error } };
    }

    return response.json();
  }
};

// Training Portal API
export const trainingAPI = {
  // Get categories
  getCategories: async () => {
    const response = await fetch(`${API_URL}/api/training-categories`, {
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to fetch categories');
    }

    return response.json();
  },

  // Get materials by category
  getMaterials: async (categoryId) => {
    const url = categoryId 
      ? `${API_URL}/api/training-materials?category_id=${categoryId}`
      : `${API_URL}/api/training-materials`;

    const response = await fetch(url, {
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to fetch materials');
    }

    return response.json();
  },

  // Get user's commissions
  getMyCommissions: async () => {
    const response = await fetch(`${API_URL}/api/commissions/my`, {
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to fetch commissions');
    }

    return response.json();
  }
};

// Admin API
export const adminAPI = {
  // Get all sales rep users
  getSalesRepUsers: async () => {
    const response = await fetch(`${API_URL}/api/admin/sales-rep-users`, {
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to fetch sales rep users');
    }

    return response.json();
  },

  // Approve/reject sales rep application
  approveSalesRep: async (applicationId, approved, notes = null) => {
    const response = await fetch(`${API_URL}/api/admin/approve-sales-rep`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ application_id: applicationId, approved, notes })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to process approval');
    }

    return response.json();
  },

  // Toggle sales rep active status
  toggleSalesRepActive: async (userId) => {
    const response = await fetch(`${API_URL}/api/admin/sales-rep-users/${userId}/toggle-active`, {
      method: 'PATCH',
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to toggle user status');
    }

    return response.json();
  },

  // Create training category
  createCategory: async (categoryData) => {
    const response = await fetch(`${API_URL}/api/training-categories`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(categoryData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create category');
    }

    return response.json();
  },

  // Delete training category
  deleteCategory: async (categoryId) => {
    const response = await fetch(`${API_URL}/api/training-categories/${categoryId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to delete category');
    }

    return response.json();
  },

  // Upload training material
  uploadMaterial: async (categoryId, title, description, file) => {
    const formData = new FormData();
    formData.append('category_id', categoryId);
    formData.append('title', title);
    formData.append('description', description);
    formData.append('file', file);

    const token = localStorage.getItem('qnc_token');
    const response = await fetch(`${API_URL}/api/training-materials`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to upload material');
    }

    return response.json();
  },

  // Delete training material
  deleteMaterial: async (materialId) => {
    const response = await fetch(`${API_URL}/api/training-materials/${materialId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to delete material');
    }

    return response.json();
  },

  // Create commission
  createCommission: async (commissionData) => {
    const response = await fetch(`${API_URL}/api/commissions`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(commissionData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create commission');
    }

    return response.json();
  },

  // Get all commissions
  getAllCommissions: async () => {
    const response = await fetch(`${API_URL}/api/commissions/all`, {
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to fetch commissions');
    }

    return response.json();
  }
};
